import { Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Layout, NavigationExtras } from '../interfaces';

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  layout$ = new BehaviorSubject<Layout>(new Layout());
  navigationExtras$ = new BehaviorSubject<NavigationExtras | null>(null);
  headerHeightChange$ = new BehaviorSubject<number>(0);
  headerCloseClicked$ = new Subject<void>();
  public headerResized$ = new Subject<void>();

  public readonly headerHeight = signal(0);

  constructor() {
    fromEvent(window, 'resize').subscribe(() => {
      this.updateHeaderHeight();
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setLayout();
      });

    this.headerResized$.subscribe(() => {
      this.updateHeaderHeight();
    });

    // Sets the layout on page refresh
    this.setLayout();
  }

  public headerCloseClick() {
    this.headerCloseClicked$.next();
  }

  private updateHeaderHeight() {
    setTimeout(() => {
      const headerHeight = document.getElementById('header')?.offsetHeight ?? 0;
      if (headerHeight !== this.headerHeight()) {
        this.headerHeight.set(headerHeight);
        this.headerHeightChange$.next(this.headerHeight());
      }
    });
  }

  private getLayout() {
    let layout = null;
    let route: ActivatedRoute | null = this.activatedRoute;
    while (route) {
      if (route.snapshot?.data?.layout) {
        layout = route.snapshot?.data?.layout;
      }
      if (route.snapshot?.data?.footer) {
        layout = { ...layout, footer: route.snapshot?.data?.footer };
      }
      if (route.snapshot?.data?.layout === 'bottomOffset') {
        layout = {
          ...layout,
          footer: {
            ...layout?.footer,
            bottomOffset: 'product',
          },
        };
      }
      if (route.snapshot?.data?.layout === 'simpleAlternativeShipping') {
        layout = {
          ...layout,
          footer: {
            ...layout?.footer,
            bottomOffset: 'checkout',
          },
        };
      }
      route = route.firstChild;
    }
    this.layout$.next(layout);
  }

  private setLayout() {
    this.getLayout();
    const state = this.router.currentNavigation()?.extras?.state;
    this.navigationExtras$.next(state as NavigationExtras);
    this.updateHeaderHeight();
  }
}
